/**
 * Volta — Demo System Manager
 * Handles loading, previewing, applying, and persisting theme demo presets.
 *
 * Usage:
 *   MilanoDemo.load('fashion')      — loads CSS + applies preset
 *   MilanoDemo.preview('beauty')    — temporarily applies for preview
 *   MilanoDemo.restore()            — reverts preview to original
 *   MilanoDemo.save('fashion')      — persists to localStorage
 *   MilanoDemo.getActive()          — returns current demo name
 *   MilanoDemo.listDemos()          — returns all 8 demo configs
 */

(function (window) {
  'use strict';

  /* ── Constants ───────────────────────────────────────────── */
  const STORAGE_KEY  = 'milano_active_demo';
  const STYLE_ID     = 'milano-demo-override';
  const BODY_PREFIX  = 'demo-';
  const ASSETS_BASE  = window.__milanoAssetsBase || (() => {
    const s = document.querySelector('script[src*="demo-system"]');
    return s ? s.src.replace(/demo-system\.js.*$/, '') : '/';
  })();

  /* ── Demo Catalogue ──────────────────────────────────────── */
  const DEMOS = {
    fashion: {
      name:        'Fashion Store',
      niche:       'fashion',
      label:       'Fashion & Apparel',
      description: 'Editorial luxury — clean lines, generous whitespace, gold accents.',
      css:         'demo-fashion.css',
      colors:      ['#1a1a1a', '#c9a96e', '#fafaf8', '#e8e4de', '#ffffff'],
      features:    ['Size guide', 'Wishlist', 'Quick view', 'Color swatches'],
      darkMode:    false
    },
    beauty: {
      name:        'Beauty & Cosmetics Store',
      niche:       'beauty',
      label:       'Beauty & Cosmetics',
      description: 'Soft feminine aesthetic with terracotta and blush tones.',
      css:         'demo-beauty.css',
      colors:      ['#e8927c', '#f7e7e0', '#fffaf9', '#6b4c3b', '#d4627e'],
      features:    ['Shade swatches', 'Before/After', 'Reviews', 'Skin quiz link'],
      darkMode:    false
    },
    electronics: {
      name:        'Electronics & Tech Store',
      niche:       'electronics',
      label:       'Electronics & Tech',
      description: 'Dark mode futuristic design with neon blue accents.',
      css:         'demo-electronics.css',
      colors:      ['#0066ff', '#00d4ff', '#0a0a0f', '#111118', '#ffffff'],
      features:    ['Compare', 'Specifications', 'Flash deals', 'Countdown timer'],
      darkMode:    true
    },
    furniture: {
      name:        'Furniture & Home Decor Store',
      niche:       'furniture',
      label:       'Furniture & Home',
      description: 'Scandinavian minimalism with warm earthy walnut tones.',
      css:         'demo-furniture.css',
      colors:      ['#4a3728', '#d4a853', '#f8f5f0', '#e4ddd4', '#ffffff'],
      features:    ['Room planner link', 'Material swatches', 'Compare', 'Wishlist'],
      darkMode:    false
    },
    food: {
      name:        'Food & Grocery Store',
      niche:       'food',
      label:       'Food & Grocery',
      description: 'Fresh and natural — farmers market aesthetic with greens and orange.',
      css:         'demo-food.css',
      colors:      ['#2d6a4f', '#f4a261', '#f9fbe7', '#d8e8c8', '#1b4332'],
      features:    ['Subscriptions', 'Nutrition info', 'Reviews', 'Same-day badge'],
      darkMode:    false
    },
    sports: {
      name:        'Sports & Fitness Store',
      niche:       'sports',
      label:       'Sports & Fitness',
      description: 'Athletic and bold — Nike/Adidas energy in red and yellow on black.',
      css:         'demo-sports.css',
      colors:      ['#ff3d00', '#ffd600', '#111111', '#1a1a1a', '#ffffff'],
      features:    ['Size guide', 'Countdown deals', 'Compare', 'Reviews'],
      darkMode:    true
    },
    jewelry: {
      name:        'Jewelry & Luxury Store',
      niche:       'jewelry',
      label:       'Jewelry & Luxury',
      description: 'Ultra-luxury black and gold — Cartier/Tiffany inspired.',
      css:         'demo-jewelry.css',
      colors:      ['#b8960c', '#e8d5a3', '#0c0c0c', '#0f0f0f', '#f0e6c8'],
      features:    ['Engraving option', 'Gift wrapping', 'Certificates', 'Zoom'],
      darkMode:    true
    },
    kids: {
      name:        'Kids & Baby Store',
      niche:       'kids',
      label:       'Kids & Baby',
      description: 'Playful and colorful — Petit Bateau/Carter\'s inspired bright aesthetic.',
      css:         'demo-kids.css',
      colors:      ['#ff6b6b', '#4ecdc4', '#fffbf0', '#ffd93d', '#45b7d1'],
      features:    ['Age filter', 'Size guide', 'Reviews', 'Safety badges'],
      darkMode:    false
    }
  };

  /* ── Internal State ──────────────────────────────────────── */
  let _active   = null;   // currently committed demo
  let _preview  = null;   // demo currently being previewed
  let _original = null;   // saved original CSS text before preview

  /* ── Helpers ─────────────────────────────────────────────── */

  /**
   * Returns or creates the <style> tag used for demo overrides.
   */
  function _getStyleTag() {
    let tag = document.getElementById(STYLE_ID);
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      document.head.appendChild(tag);
    }
    return tag;
  }

  /**
   * Fetches a CSS file from the assets directory and injects it.
   * Returns a Promise that resolves when injection is complete.
   */
  function _loadCSSFile(filename) {
    return new Promise((resolve, reject) => {
      const url = ASSETS_BASE + filename;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error('CSS fetch failed: ' + url);
          return res.text();
        })
        .then(cssText => {
          _getStyleTag().textContent = cssText;
          resolve(cssText);
        })
        .catch(err => {
          console.warn('[MilanoDemo] Could not fetch CSS:', err);
          reject(err);
        });
    });
  }

  /**
   * Fades the page out then back in, executing a callback at the midpoint.
   */
  function _fadeTransition(callback) {
    const el = document.documentElement;
    el.style.transition = 'opacity 0.3s ease';
    el.style.opacity    = '0';
    setTimeout(() => {
      if (typeof callback === 'function') callback();
      el.style.opacity = '1';
    }, 300);
    // Clean up after transition
    setTimeout(() => {
      el.style.transition = '';
    }, 650);
  }

  /**
   * Applies CSS custom property overrides from a color map onto :root.
   */
  function _applyCSSVars(colors) {
    if (!colors) return;
    const root = document.documentElement;
    const map = {
      primary:      '--color-primary',
      accent:       '--color-accent',
      background:   '--color-background',
      text:         '--color-text',
      heading:      '--color-heading',
      border:       '--color-border',
      button_bg:    '--color-button-bg',
      button_text:  '--color-button-text'
    };
    Object.keys(map).forEach(key => {
      if (colors[key]) {
        root.style.setProperty(map[key], colors[key]);
      }
    });
  }

  /**
   * Clears inline CSS variable overrides from :root.
   */
  function _clearCSSVars() {
    const root = document.documentElement;
    const vars = [
      '--color-primary', '--color-accent', '--color-background',
      '--color-text', '--color-heading', '--color-border',
      '--color-button-bg', '--color-button-text'
    ];
    vars.forEach(v => root.style.removeProperty(v));
  }

  /**
   * Sets the body class to indicate the active demo.
   */
  function _setBodyClass(demoName) {
    // Remove all previous demo body classes
    const toRemove = [];
    document.body.classList.forEach(cls => {
      if (cls.startsWith(BODY_PREFIX)) toRemove.push(cls);
    });
    toRemove.forEach(cls => document.body.classList.remove(cls));

    if (demoName) {
      document.body.classList.add(BODY_PREFIX + demoName);
      document.body.setAttribute('data-milano-demo', demoName);
    } else {
      document.body.removeAttribute('data-milano-demo');
    }
  }

  /**
   * Saves current style tag text so it can be restored on restore().
   */
  function _saveOriginal() {
    const tag = document.getElementById(STYLE_ID);
    _original = tag ? tag.textContent : '';
  }

  /**
   * Dispatches a custom event when the demo changes.
   */
  function _dispatch(eventName, detail) {
    document.dispatchEvent(new CustomEvent('milano:demo:' + eventName, {
      bubbles: true,
      detail:  detail || {}
    }));
  }

  /* ── Public API ──────────────────────────────────────────── */
  const MilanoDemo = {

    /**
     * Load and apply a demo by name. Commits the choice.
     * @param {string} demoName  — one of the 8 demo keys
     * @returns {Promise}
     */
    load(demoName) {
      const demo = DEMOS[demoName];
      if (!demo) {
        console.warn('[MilanoDemo] Unknown demo:', demoName);
        return Promise.reject(new Error('Unknown demo: ' + demoName));
      }

      return new Promise((resolve) => {
        _fadeTransition(() => {
          _loadCSSFile(demo.css)
            .then(() => {
              _active   = demoName;
              _preview  = null;
              _applyCSSVars(demo.colors ? { primary: demo.colors[0], accent: demo.colors[1], background: demo.colors[2] } : null);
              _setBodyClass(demoName);
              _dispatch('change', { demo: demoName, config: demo });
              resolve(demo);
            })
            .catch(() => {
              // CSS load failed — still apply body class and resolve
              _active = demoName;
              _setBodyClass(demoName);
              _dispatch('change', { demo: demoName, config: demo });
              resolve(demo);
            });
        });
      });
    },

    /**
     * Apply preset data directly (e.g. from loaded JSON).
     * @param {object} presetData  — parsed preset JSON
     */
    apply(presetData) {
      if (!presetData) return;
      _applyCSSVars(presetData.colors || null);
      if (presetData.demo_css) {
        _loadCSSFile(presetData.demo_css).catch(() => {});
      }
      if (presetData.niche) {
        _setBodyClass(presetData.niche);
      }
    },

    /**
     * Temporarily apply a demo for preview without committing.
     * @param {string} demoName
     * @returns {Promise}
     */
    preview(demoName) {
      const demo = DEMOS[demoName];
      if (!demo) return Promise.reject(new Error('Unknown demo: ' + demoName));

      // Save original on first preview call
      if (_preview === null) {
        _saveOriginal();
      }

      _preview = demoName;

      return _loadCSSFile(demo.css)
        .then(() => {
          _applyCSSVars(demo.colors ? { primary: demo.colors[0], accent: demo.colors[1], background: demo.colors[2] } : null);
          _setBodyClass(demoName);
          _dispatch('preview', { demo: demoName, config: demo });
          return demo;
        })
        .catch(() => {
          _setBodyClass(demoName);
          _dispatch('preview', { demo: demoName, config: demo });
          return demo;
        });
    },

    /**
     * Restore the theme to its state before preview was called.
     */
    restore() {
      if (_preview === null) return;

      _fadeTransition(() => {
        const tag = _getStyleTag();
        tag.textContent = _original || '';
        _clearCSSVars();
        _setBodyClass(_active);
        _preview  = null;
        _original = null;
        _dispatch('restore', { demo: _active });
      });
    },

    /**
     * Commit the active (or previewed) demo and persist to localStorage.
     * @param {string} [demoName]  — defaults to currently previewed or active demo
     */
    save(demoName) {
      const nameToSave = demoName || _preview || _active;
      if (!nameToSave) return;

      try {
        localStorage.setItem(STORAGE_KEY, nameToSave);
      } catch (e) {}

      if (nameToSave !== _active) {
        this.load(nameToSave);
      }

      _dispatch('save', { demo: nameToSave });
    },

    /**
     * Returns the currently active demo name (from localStorage if set).
     * @returns {string|null}
     */
    getActive() {
      if (_active) return _active;
      try {
        return localStorage.getItem(STORAGE_KEY) || null;
      } catch (e) {
        return null;
      }
    },

    /**
     * Returns all 8 demo configuration objects.
     * @returns {object}
     */
    listDemos() {
      return Object.assign({}, DEMOS);
    },

    /**
     * Returns a single demo config by key.
     * @param {string} demoName
     * @returns {object|null}
     */
    getDemo(demoName) {
      return DEMOS[demoName] || null;
    },

    /**
     * Clears the active demo and removes all overrides.
     */
    clear() {
      _fadeTransition(() => {
        const tag = document.getElementById(STYLE_ID);
        if (tag) tag.textContent = '';
        _clearCSSVars();
        _setBodyClass(null);
        _active  = null;
        _preview = null;

        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        _dispatch('clear', {});
      });
    },

    /**
     * Initialize the demo system on page load.
     * Re-applies the persisted demo if one was previously saved.
     */
    init() {
      const saved = this.getActive();
      if (saved && DEMOS[saved]) {
        this.load(saved);
      }

      // Inject design-mode switcher if in Shopify customizer
      if (window.Shopify && window.Shopify.designMode) {
        this._injectDesignModeSwitcher();
      }
    },

    /**
     * Injects a floating demo-switcher button in Shopify design mode.
     * @private
     */
    _injectDesignModeSwitcher() {
      if (document.getElementById('milano-demo-switcher')) return;

      const btn = document.createElement('button');
      btn.id          = 'milano-demo-switcher';
      btn.textContent = 'Switch Demo';
      btn.setAttribute('aria-label', 'Open demo switcher');
      btn.style.cssText = [
        'position:fixed',
        'bottom:24px',
        'right:24px',
        'z-index:99999',
        'background:#1a1a1a',
        'color:#fff',
        'border:none',
        'border-radius:50px',
        'padding:12px 22px',
        'font-size:14px',
        'font-weight:600',
        'cursor:pointer',
        'box-shadow:0 4px 24px rgba(0,0,0,0.25)',
        'letter-spacing:0.04em',
        'transition:all 0.2s ease'
      ].join(';');

      btn.addEventListener('mouseenter', () => {
        btn.style.background = '#333';
        btn.style.transform  = 'scale(1.04)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = '#1a1a1a';
        btn.style.transform  = 'scale(1)';
      });

      btn.addEventListener('click', () => {
        // Try to navigate to demo selector page if it exists
        const selectorUrl = '/pages/demo-selector';
        // If we're already there, just scroll to top
        if (window.location.pathname.includes('demo-selector')) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          window.location.href = selectorUrl;
        }
      });

      document.body.appendChild(btn);
    }
  };

  /* ── Auto-init on DOM ready ──────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MilanoDemo.init());
  } else {
    MilanoDemo.init();
  }

  /* ── Expose globally ─────────────────────────────────────── */
  window.MilanoDemo = MilanoDemo;

})(window);
